import { LeagueTier } from '../interfaces/creature.interface';
import { QuizSubject } from '../interfaces/question.interface';
import { DailyBonus } from '../interfaces/score.interface';

export const LEVEL_THRESHOLDS: number[] = [
  0,      // Level 0
  50,     // Level 1
  120,    // Level 2
  210,    // Level 3
  320,    // Level 4
  450,    // Level 5
  600,    // Level 6
  780,    // Level 7
  990,    // Level 8
  1230,   // Level 9
  1500,   // Level 10
  1800,   // Level 11
  2140,   // Level 12
  2520,   // Level 13
  2940,   // Level 14
  3400,   // Level 15
  3920,   // Level 16
  4500,   // Level 17
  5140,   // Level 18
  5840,   // Level 19
  6600,   // Level 20
  7400,   // Level 21
  8300,   // Level 22
  9300,   // Level 23
  10400,  // Level 24
  11600,  // Level 25
];

export const LEAGUE_CONFIG: { tier: LeagueTier; minLevel: number; maxLevel: number; color: string; label: { en: string; ar: string } }[] = [
  { tier: LeagueTier.WOOD, minLevel: 0, maxLevel: 4, color: '#8B6914', label: { en: 'Wood', ar: 'خشبي' } },
  { tier: LeagueTier.BRONZE, minLevel: 5, maxLevel: 9, color: '#CD7F32', label: { en: 'Bronze', ar: 'برونزي' } },
  { tier: LeagueTier.SILVER, minLevel: 10, maxLevel: 14, color: '#C0C0C0', label: { en: 'Silver', ar: 'فضي' } },
  { tier: LeagueTier.GOLD, minLevel: 15, maxLevel: 19, color: '#FFD700', label: { en: 'Gold', ar: 'ذهبي' } },
  { tier: LeagueTier.DIAMOND, minLevel: 20, maxLevel: 25, color: '#B9F2FF', label: { en: 'Diamond', ar: 'ماسي' } },
];

export function getLeagueTier(level: number): LeagueTier {
  if (level < 5) return LeagueTier.WOOD;
  if (level < 10) return LeagueTier.BRONZE;
  if (level < 15) return LeagueTier.SILVER;
  if (level < 20) return LeagueTier.GOLD;
  return LeagueTier.DIAMOND;
}

export function getLeagueConfig(tier: LeagueTier) {
  return LEAGUE_CONFIG.find(l => l.tier === tier);
}

export function getXPForLevel(level: number): number {
  if (level < 0) return 0;
  if (level >= LEVEL_THRESHOLDS.length) return LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1];
  return LEVEL_THRESHOLDS[level];
}

export function getXPForNextLevel(level: number): number {
  const nextLevel = level + 1;
  if (nextLevel >= LEVEL_THRESHOLDS.length) return LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1];
  return LEVEL_THRESHOLDS[nextLevel];
}

export function getLevelFromXP(totalXP: number): number {
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (totalXP >= LEVEL_THRESHOLDS[i]) return i;
  }
  return 0;
}

export function getLevelProgress(totalXP: number): number {
  const level = getLevelFromXP(totalXP);
  const currentLevelXP = getXPForLevel(level);
  const nextLevelXP = getXPForNextLevel(level);
  if (nextLevelXP === currentLevelXP) return 100;
  return Math.round(((totalXP - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100);
}

export function getDailyBonusTopic(): DailyBonus {
  const dayOfWeek = new Date().getDay();
  const bonuses: DailyBonus[] = [
    { subject: 'random', multiplier: 1.5, label: { en: 'Random Mix Day', ar: 'يوم المزيج العشوائي' } },
    { subject: QuizSubject.MATH, multiplier: 1.5, label: { en: 'Math Day', ar: 'يوم الرياضيات' } },
    { subject: QuizSubject.SCIENCE, multiplier: 1.5, label: { en: 'Science Day', ar: 'يوم العلوم' } },
    { subject: QuizSubject.ARABIC, multiplier: 1.5, label: { en: 'Arabic Day', ar: 'يوم اللغة العربية' } },
    { subject: QuizSubject.ENGLISH, multiplier: 1.5, label: { en: 'English Day', ar: 'يوم اللغة الإنجليزية' } },
    { subject: QuizSubject.ISLAMIC, multiplier: 1.5, label: { en: 'Islamic Studies Day', ar: 'يوم الدراسات الإسلامية' } },
    { subject: QuizSubject.GENERAL, multiplier: 1.5, label: { en: 'General Knowledge Day', ar: 'يوم المعرفة العامة' } },
  ];
  return bonuses[dayOfWeek];
}

export const QUESTIONS_PER_SESSION = 7;
export const DEFAULT_TIME_LIMIT = 30; // seconds
